import { getApiBase } from './config';
import { getAuthHeaders } from './auth';

const API_BASE = getApiBase();

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  const r = await fetch(url, {
    ...options,
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options?.headers,
    } as HeadersInit,
  });
  const text = await r.text();
  if (!r.ok) {
    let msg = `خطا: ${r.status}`;
    try {
      if (text) msg = JSON.parse(text).message ?? msg;
    } catch {}
    throw new Error(msg);
  }
  return text ? JSON.parse(text) : ({} as T);
}

export interface BidGuideline {
  id: number;
  code: string;
  name: string;
  description: string | null;
  engine: string;
  config: Record<string, number>;
}

export interface BidderInput {
  name: string;
  price: string;
  is_self: boolean;
}

export interface SuggestedBidder {
  name: string;
  price: number;
  is_self: boolean;
}

export interface BidderResult {
  index: number;
  name: string;
  price: number;
  is_self: boolean;
  ratio_to_po_pct?: number;
  normalized?: number;
  in_domain?: boolean;
  in_initial_range?: boolean;
  in_norm_domain?: boolean;
  compatibility_method?: string | null;
  pct_diff_from_po?: number;
  excluded_reason?: string | null;
}

export interface BidDomainResult {
  success: boolean;
  article: string;
  article_title: string;
  summary: string;
  Po: number;
  n: number;
  n_total?: number;
  mean?: number;
  mean_ratio_to_po_pct?: number;
  mean_valid_pct_range?: { min: number; max: number };
  mean_valid_range?: { min: number; max: number };
  mo?: number;
  std_dev?: number;
  Po_normalized?: number;
  Po_in_norm_domain?: boolean;
  exception_pct?: number;
  normalization_applied?: boolean;
  winner?: BidderResult | null;
  second_winner?: BidderResult | null;
  bidders: BidderResult[];
  in_domain_bidders?: BidderResult[];
  initial_range?: { min: number; max: number };
  norm_domain?: { min: number; max: number };
  all_in_initial_range?: boolean;
  needs_reestimate?: boolean;
  needs_committee_review?: boolean;
  proceed_to_article_7?: boolean;
  regulation_notes?: string[];
}

export const bidDomainApi = {
  guidelines: () =>
    fetchApi<{ success: boolean; data: BidGuideline[] }>('/bid-domain/guidelines'),

  projectContext: (projectId: number) =>
    fetchApi<{
      success: boolean;
      data: {
        analyses: Array<{ id: number; name: string; total_cost: string; currency: string | null }>;
        company_name: string;
      };
    }>(`/bid-domain/projects/${projectId}/context`),

  calculate: (payload: {
    guideline_id: number;
    estimate_at_tender: number;
    bidders: Array<{ name?: string; price: number; is_self?: boolean }>;
    project_id?: number;
    cost_analysis_id?: number;
    save?: boolean;
    title?: string;
  }) =>
    fetchApi<{ success: boolean; result: BidDomainResult; guideline: BidGuideline; evaluation_id?: number }>(
      '/bid-domain/calculate',
      { method: 'POST', body: JSON.stringify(payload) }
    ),

  aiAdvice: (payload: {
    guideline_id: number;
    estimate_at_tender: number;
    bidders: Array<{ name?: string; price: number; is_self?: boolean }>;
    result: BidDomainResult;
    question?: string;
  }) =>
    fetchApi<{ success: boolean; reply?: string; message?: string }>(
      '/bid-domain/ai-advice',
      { method: 'POST', body: JSON.stringify(payload) }
    ),

  aiAssistant: (payload: {
    guideline_id: number;
    estimate_at_tender: number;
    bidders?: Array<{ name?: string; price: number; is_self?: boolean }>;
    result?: BidDomainResult;
    message: string;
    conversation?: Array<{ role: 'user' | 'assistant'; content: string }>;
    project_id?: number;
    project_name?: string;
  }) =>
    fetchApi<{
      success: boolean;
      reply?: string;
      suggested_po?: number | null;
      suggested_bidders?: SuggestedBidder[];
      analysis_points?: string[];
      message?: string;
    }>('/bid-domain/ai-assistant', { method: 'POST', body: JSON.stringify(payload) }),

  speechToText: async (audioBlob: Blob, mimeType = 'audio/wav') => {
    const form = new FormData();
    const ext = mimeType.includes('wav')
      ? 'wav'
      : mimeType.includes('ogg')
        ? 'ogg'
        : mimeType.includes('mp4') || mimeType.includes('m4a')
          ? 'm4a'
          : mimeType.includes('mpeg') || mimeType.includes('mp3')
            ? 'mp3'
            : 'webm';
    form.append('audio', audioBlob, `recording.${ext}`);
    form.append('language', 'fa');

    const url = `${API_BASE}/bid-domain/speech-to-text`;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(getAuthHeaders() as Record<string, string>),
    };
    // Content-Type را خود مرورگر برای FormData می‌گذارد (با boundary)
    delete headers['Content-Type'];

    const r = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers,
      body: form,
    });
    const text = await r.text();
    if (!r.ok) {
      let msg = `خطا: ${r.status}`;
      try {
        if (text) msg = JSON.parse(text).message ?? msg;
      } catch {}
      throw new Error(msg);
    }
    return text ? (JSON.parse(text) as { success: boolean; text?: string; message?: string }) : { success: false };
  },
};
